import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useCan } from '@/auth/consoleCapabilityContext';
import { PageHeader, Segmented } from '@/components/ui';
import { AccountAuditPanel } from './Audit/AccountAuditPanel';
import { AdminAuditPanel } from './Audit/AdminAuditPanel';

type AuditSource = 'account' | 'admin';

/**
 * EAA-1821 (design frame S8): `/console/audit` — read-only audit trail over
 * TWO separate append-only tables (`AccountAudit` via `/accounts/audit`,
 * `AdminAudit` via `/admin/audit`), each paginated independently server-side.
 *
 * A merged, correctly-paginated single timeline is NOT derivable client-side:
 * two independently-paged lists interleaved and labelled "page 2" would
 * silently drop or duplicate rows the moment their per-source page counts
 * diverge. The spec demands an honest server-side total ("Hiển thị N / total
 * bản ghi"), so this screen shows one source at a time via a segmented
 * control instead of faking a combined feed — see the ticket report for the
 * full reasoning; a real combined endpoint is optional future work (EAA-1829).
 *
 * The chosen source is URL-backed (`?source=account|admin`, default
 * `account`) so it survives reload and is linkable, mirroring the role-map
 * screen's `?view=` convention from the design handoff.
 *
 * Gating: `console.audit.view` gates render AND fetch (fail-closed) — this
 * component returns null before either child panel mounts, and each panel
 * repeats the same `can()` check in its own query `enabled` as defense in
 * depth.
 */
export function AuditPage() {
    const can = useCan();
    const { t } = useTranslation();
    const [searchParams, setSearchParams] = useSearchParams();

    // EAA-1845: options are built here (not at module scope) because the labels
    // must go through `t()`, which is only available inside the component.
    const sourceOptions = [
        { label: t('console.audit.sourceAccount'), value: 'account' },
        { label: t('console.audit.sourceAdmin'), value: 'admin' },
    ];

    const rawSource = searchParams.get('source');
    const source: AuditSource = rawSource === 'admin' ? 'admin' : 'account';

    const setSource = useCallback(
        (next: AuditSource) => {
            setSearchParams((prev) => {
                const params = new URLSearchParams(prev);
                if (next === 'account') {
                    // Keep the default source out of the URL so a plain
                    // `/console/audit` link stays canonical.
                    params.delete('source');
                } else {
                    params.set('source', next);
                }
                return params;
            });
        },
        [setSearchParams]
    );

    if (!can('console.audit.view')) {
        return null;
    }

    return (
        // EAA-1821: `--fit` makes the page exactly as tall as the console shell
        // so the log table owns the ONLY scrollbar. Without it a 200-row page
        // scrolled the whole document, taking the sidebar, the topbar and the
        // filter bar with it — the two controls an operator needs most while
        // reading down a long audit list.
        <main className="portal-admin portal-console-page portal-console-page--fit">
            <PageHeader
                title={t('console.audit.title')}
                subtitle={t('console.audit.subtitle')}
                actions={
                    <Segmented
                        value={source}
                        onChange={(value) => setSource(value as AuditSource)}
                        options={sourceOptions}
                    />
                }
            />

            {/* The one region allowed to absorb the leftover height; everything
                else on this page keeps its natural size. `22-fit-to-shell.css`
                gives `.fit-grow` and its direct child the `flex:1; min-height:0`
                chain the panel needs to hand that height down to the table. */}
            <div className="fit-grow">
                {source === 'account' ? <AccountAuditPanel /> : <AdminAuditPanel />}
            </div>
        </main>
    );
}
